import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Landmark,
  CreditCard,
  ChevronRight,
  Shield,
  Loader2,
  Sparkles,
} from "lucide-react";
import { useTransferFlow } from "../../context/TransferFlowContext";
import { useMe, useRecipient } from "../../hooks/useMe";
import { publicQuoteService } from "../../services/publicQuote.service";
import { PrivyCardOnrampButton } from "./PrivyCardOnrampButton";
import { ErrorNotice } from "../errors/ErrorNotice";
const KYC_ROUTE = "/kyc";

const SectionLabel = ({ children }) => (
  <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A4730]/40">
    {children}
  </h2>
);

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/60 mb-1">
    {children}
  </label>
);

const KYC_COPY = {
  not_started: {
    title: "Verify your identity to send money",
    sub: "You'll need to complete a quick identity check before you can transfer funds.",
    cta: "Verify identity",
  },
  pending: {
    title: "Verification in review",
    sub: "We're reviewing your documents. This usually takes a few minutes - try again shortly.",
    cta: "Check status",
  },
  rejected: {
    title: "Verification failed",
    sub: "Your last submission couldn't be verified. Please resubmit your documents.",
    cta: "Resubmit",
  },
  expired: {
    title: "Verification expired",
    sub: "Your verification has expired. Please verify your identity again.",
    cta: "Verify again",
  },
};

const ZUBA_XAF_FUNDING_SOURCES = [
  { value: "salary", label: "Salary" },
  { value: "business_income", label: "Business income" },
  { value: "investments", label: "Investments" },
];

function splitFullName(fullName = "") {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

export function Transfer() {
  const navigate = useNavigate();
  const location = useLocation();

  const { recipientId, transferId, setTransferId, setQuote } =
    useTransferFlow();
  const { recipient, paymentMethods, isLoadingRecipient } =
    useRecipient(recipientId);
  const {
    profile,
    isLoadingProfile,
    updateMeAsync,
    isUpdatingMe,
    createTransferAsync,
    isCreatingTransfer,
    kycProfiles,
    isLoadingKyc,
    prepareTransferAsync,
    isPreparingTransfer,
  } = useMe();

  useEffect(() => {
    if (!recipientId) navigate("/home", { replace: true });
  }, [recipientId, navigate]);

  const [sendCurrency, setSendCurrency] = useState(
    location.state?.selectedSendCurrency || profile?.default_currency || "GBP",
  );
  const [receiveCurrency, setReceiveCurrency] = useState(
    recipient?.currency || "KES",
  );

  // Active funding option state: "bank" or "card"
  const [fundingChoice, setFundingChoice] = useState("bank");
  const [amount, setAmount] = useState("100");
  const [formError, setFormError] = useState(null);

  // Live Quote States for protection calculation preview
  const [quoteData, setQuoteData] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Card flow PIN modal state
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState(null);

  const [showSenderDetails, setShowSenderDetails] = useState(false);
  const [senderDetailsError, setSenderDetailsError] = useState(null);
  const [senderDetails, setSenderDetails] = useState(() => ({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    countryCode: "",
    gender: "",
    sourceOfFunds: "",
  }));

  useEffect(() => {
    if (location.state?.selectedSendCurrency) {
      setSendCurrency(location.state.selectedSendCurrency);
    }
  }, [location.state]);

  useEffect(() => {
    if (profile?.default_currency && !location.state?.selectedSendCurrency) {
      setSendCurrency(profile.default_currency);
    }
  }, [profile?.default_currency]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (recipient?.currency) {
      setReceiveCurrency(recipient.currency);
    }
  }, [recipient?.currency]);

  useEffect(() => {
    const name = splitFullName(profile?.full_name);
    setSenderDetails({
      firstName: name.firstName,
      lastName: name.lastName,
      phoneNumber: profile?.phone_number || "",
      countryCode: profile?.country_code || "",
      gender: profile?.gender || "",
      sourceOfFunds: profile?.source_of_funds || "",
    });
  }, [profile]);

  // Fetch Live Quote Preview
  const fetchLiveQuote = useCallback(
    async (currentAmount, currentSendCurr, currentReceiveCurr, destCountry) => {
      if (!currentAmount || Number(currentAmount) <= 0) return;
      setQuoteLoading(true);
      try {
        const result = await publicQuoteService.preview({
          send_amount: currentAmount,
          send_currency: currentSendCurr,
          receive_currency: currentReceiveCurr,
          destination_country: destCountry || "KE",
        });
        setQuoteData(result);
      } catch {
        setQuoteData(null);
      } finally {
        setQuoteLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLiveQuote(
        amount,
        sendCurrency,
        receiveCurrency,
        recipient?.country_code,
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [
    amount,
    sendCurrency,
    receiveCurrency,
    recipient?.country_code,
    fetchLiveQuote,
  ]);

  const openCurrencyPicker = () => {
    navigate("/currencypicker", {
      state: {
        type: "send",
        currentSend: sendCurrency,
      },
    });
  };

  const defaultPaymentMethod =
    paymentMethods.find((pm) => pm.is_default) || paymentMethods[0];

  const submitting = isCreatingTransfer || isPreparingTransfer;
  const isCameroonXafTransfer =
    recipient?.country_code === "CM" && receiveCurrency === "XAF";
  const senderName = splitFullName(profile?.full_name);
  const hasSupportedFundingSource = ZUBA_XAF_FUNDING_SOURCES.some(
    ({ value }) => value === profile?.source_of_funds,
  );
  const isCameroonSenderProfileComplete =
    Boolean(senderName.firstName && senderName.lastName) &&
    Boolean(profile?.phone_number) &&
    Boolean(profile?.country_code) &&
    Boolean(profile?.gender) &&
    hasSupportedFundingSource;

  function requireCameroonSenderDetails() {
    if (!isCameroonXafTransfer || isCameroonSenderProfileComplete) {
      return false;
    }

    setSenderDetailsError(null);
    setShowSenderDetails(true);
    return true;
  }

  async function saveCameroonSenderDetails(event) {
    event.preventDefault();
    setSenderDetailsError(null);

    const firstName = senderDetails.firstName.trim();
    const lastName = senderDetails.lastName.trim();
    const phoneNumber = senderDetails.phoneNumber.trim();
    const countryCode = senderDetails.countryCode.trim().toUpperCase();

    if (
      !firstName ||
      !lastName ||
      !phoneNumber ||
      !countryCode ||
      !senderDetails.gender ||
      !senderDetails.sourceOfFunds
    ) {
      setSenderDetailsError(
        "Complete every field to continue with this Cameroon transfer.",
      );
      return;
    }

    try {
      await updateMeAsync({
        full_name: `${firstName} ${lastName}`,
        phone_number: phoneNumber,
        country_code: countryCode,
        gender: senderDetails.gender,
        source_of_funds: senderDetails.sourceOfFunds,
      });
      setShowSenderDetails(false);
    } catch (err) {
      setSenderDetailsError(err);
    }
  }

  // Step 1: validate the form and open the PIN modal for card funding.
  function handleInitializeCardTransfer() {
    setFormError(null);
    if (requireCameroonSenderDetails()) return;
    const parsedSend = parseFloat(amount);
    if (!parsedSend || parsedSend <= 0) {
      setFormError("Enter a valid amount to send.");
      return;
    }
    if (!defaultPaymentMethod) {
      setFormError("This recipient has no payment method set up yet.");
      return;
    }
    setPin("");
    setPinError(null);
    setShowPinModal(true);
  }

  // Step 2: once the PIN is entered, create + prepare the transfer.
  async function submitCardTransferWithPin() {
    if (!/^\d{4,12}$/.test(pin)) {
      setPinError("Enter your 4–12 digit PIN.");
      return;
    }

    setPinError(null);

    try {
      const parsedSend = parseFloat(amount);

      const transfer = await createTransferAsync({
        recipient_id: recipientId,
        recipient_payment_method_id: defaultPaymentMethod.id,
        funding_method: "card",
        send_amount: parsedSend,
        send_currency: sendCurrency,
        receive_currency: receiveCurrency,
      });

      await prepareTransferAsync({
        transferId: transfer.id,
        pin,
      });
      setTransferId(transfer.id);
      setQuote(transfer.quote ?? transfer);
      setShowPinModal(false);
      setPin("");
    } 
    catch (err) {
      setPinError(err);
    }
    }


  async function handleBankTransferContinue() {
    setFormError(null);
    if (requireCameroonSenderDetails()) return;
    const parsedSend = parseFloat(amount);
    if (!parsedSend || parsedSend <= 0) {
      setFormError("Enter an amount for your recipient to receive.");
      return;
    }
    if (!defaultPaymentMethod) {
      setFormError("This recipient has no payment method set up yet.");
      return;
    }

    try {
      const transfer = await createTransferAsync({
        recipient_id: recipientId,
        recipient_payment_method_id: defaultPaymentMethod.id,
        funding_method: "bank",
        send_amount: parsedSend,
        send_currency: sendCurrency,
        receive_currency: receiveCurrency,
      });
      setTransferId(transfer.id);
      setQuote(transfer.quote ?? transfer);
      navigate("/transactionsummary");
    } catch (err) {
      setFormError(err);
    }
  }

  // Bridge is BluPay's sole identity-verification provider. Do not use the
  // first profile returned here: that could be a legacy/provider-specific
  // record and would incorrectly unlock transfers.
  const bridgeKyc = kycProfiles.find(
    (kycProfile) =>
      String(kycProfile.provider || "").toLowerCase() === "bridge",
  );
  const kycState = bridgeKyc?.status ?? "not_started";
  const isKycVerified = kycState === "approved";

  if (isLoadingRecipient || isLoadingKyc || isLoadingProfile) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <p className="text-sm text-[#0A4730]/40">Loading…</p>
      </div>
    );
  }

  if (!isKycVerified) {
    const copy = KYC_COPY[kycState] ?? KYC_COPY.not_started;
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-6">
          <Shield size={26} className="text-amber-500" />
        </div>
        <h1 className="text-xl font-serif font-semibold text-[#0A4730] mb-2">
          {copy.title}
        </h1>
        <p className="text-sm text-[#0A4730]/50 max-w-xs mb-8 leading-relaxed">
          {copy.sub}
        </p>
        <button
          onClick={() => navigate(KYC_ROUTE)}
          className="w-full max-w-xs h-14 rounded-2xl bg-[#0A4730] text-white font-bold"
        >
          {copy.cta}
        </button>
        <button
          onClick={() => navigate("/home")}
          className="mt-3 text-sm font-semibold text-[#0A4730]/60 underline underline-offset-2"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 text-left">
      {/* HEADER — mobile only */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[40px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft className="text-white" size={20} />
          </button>
          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">Payments</p>
            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              Transfer Money
            </h1>
          </div>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="hidden md:flex items-center gap-3 max-w-xl mx-auto px-5 pt-10 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full border border-slate-200 bg-transparent flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
        >
          <ChevronLeft size={18} />
        </button>
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
          Transfer Money
        </h1>
      </div>

      {/* CONTENT */}
      <div className="max-w-xl mx-auto px-5 py-8 space-y-6">
        {/* RECIPIENT */}
        {recipient && (
          <div className="space-y-2">
            <SectionLabel>Sending To</SectionLabel>
            <div className="bg-transparent rounded-3xl border border-[#0A4730]/10 px-6 py-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#0A4730]/5 flex items-center justify-center shrink-0">
                <span className="text-sm font-semibold text-[#0A4730]">
                  {(recipient.nickname ||
                    recipient.full_name ||
                    "?")[0].toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0A4730]">
                  {recipient.nickname || recipient.full_name}
                </p>
                <p className="text-xs text-[#0A4730]/50 mt-0.5">
                  {recipient.full_name}
                  {recipient.country_code ? ` · ${recipient.country_code}` : ""}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* PAY WITH TOGGLE */}
        <div className="space-y-2">
          <SectionLabel>Pay With</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => {
                setFundingChoice("bank");
                setTransferId(null);
              }}
              className={`p-4 rounded-3xl text-left transition flex flex-col justify-between bg-transparent ${
                fundingChoice === "bank"
                  ? "border-2 border-[#0A4730] shadow-sm"
                  : "border border-[#0A4730]/15 hover:border-[#0A4730]/40"
              }`}
            >
              <div className="w-9 h-9 rounded-xl bg-[#0A4730]/10 flex items-center justify-center text-[#0A4730] mb-3">
                <Landmark size={17} />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0A4730]">
                  Bank Transfer
                </p>
                <p className="text-[11px] text-[#0A4730]/50 mt-0.5">
                  Manual deposit instructions
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => {
                setFundingChoice("card");
                setTransferId(null);
              }}
              className={`p-4 rounded-3xl text-left transition flex flex-col justify-between bg-transparent ${
                fundingChoice === "card"
                  ? "border-2 border-[#0A4730] shadow-sm"
                  : "border border-[#0A4730]/15 hover:border-[#0A4730]/40"
              }`}
            >
              <div className="w-9 h-9 rounded-xl bg-[#0A4730]/10 flex items-center justify-center text-[#0A4730] mb-3">
                <CreditCard size={17} />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0A4730]">
                  Card Payment
                </p>
                <p className="text-[11px] text-[#0A4730]/50 mt-0.5">
                  Instant pay via Privy
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* TRANSFER DETAILS + LIVE FX CALCULATION BAR */}
        <div className="space-y-2">
          <SectionLabel>Transfer Details</SectionLabel>
          <div className="bg-transparent rounded-3xl border border-[#0A4730]/10 p-5 space-y-4">
            {/* You Send Input */}
            <div className="p-4 rounded-2xl bg-transparent border border-[#0A4730]/15 transition duration-200">
              <FieldLabel>You Send</FieldLabel>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    if (transferId && fundingChoice === "card")
                      setTransferId(null);
                  }}
                  placeholder="0.00"
                  min="1"
                  step="0.01"
                  className="bg-transparent text-2xl font-bold outline-none w-full text-[#0A4730] placeholder-[#0A4730]/20"
                />
                <button
                  type="button"
                  onClick={openCurrencyPicker}
                  className="flex items-center gap-1.5 pl-3 pr-2.5 py-2 rounded-xl bg-transparent border border-[#0A4730]/20 text-xs font-bold text-[#0A4730] hover:border-[#0A4730]/40 active:scale-[0.98] transition whitespace-nowrap"
                >
                  {sendCurrency}
                  <ChevronRight size={13} className="text-[#0A4730]/40" />
                </button>
              </div>
            </div>

            {/* BRIDGE RATE BAR */}
            <div className="relative flex items-center justify-center py-0.5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#0A4730]/10" />
              </div>

              <div className="relative flex items-center gap-2 rounded-xl bg-[#FDFDFD] border border-[#0A4730]/15 px-3 py-1.5 text-xs text-[#0A4730]">
                {quoteLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2
                      className="animate-spin text-[#0A4730]"
                      size={13}
                    />
                    <span className="font-medium">Updating live rate...</span>
                  </div>
                ) : quoteData ? (
                  <div className="flex items-center flex-wrap justify-center gap-2 text-center">
                    <span className="font-semibold">
                      1 {quoteData.send_currency} ={" "}
                      {Number(quoteData.exchange_rate).toFixed(4)}{" "}
                      {quoteData.receive_currency}
                    </span>
                    <span className="text-[#0A4730]/30">•</span>
                    <span className="text-[#0A4730]/80 font-medium flex items-center gap-1">
                      <Sparkles size={12} className="text-[#0A4730]" /> Our FX
                      rate, no fees
                    </span>
                  </div>
                ) : (
                  <span className="text-[#0A4730]/40">
                    Enter amount to see live rates
                  </span>
                )}
              </div>
            </div>

            {/* Recipient Gets Output */}
            <div className="p-4 rounded-2xl bg-transparent border border-[#0A4730]/15 transition duration-200">
              <FieldLabel>Recipient Gets (Estimated)</FieldLabel>
              <div className="flex items-center justify-between mt-1">
                <span className="text-2xl font-bold text-[#0A4730]">
                  {quoteData && !quoteLoading
                    ? Number(quoteData.receive_amount).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )
                    : "0.00"}
                </span>
                <span className="text-xs font-bold text-[#0A4730] bg-transparent px-3 py-2 rounded-xl border border-[#0A4730]/20">
                  {receiveCurrency}
                </span>
              </div>
            </div>
          </div>
        </div>

        <ErrorNotice
          error={formError}
          fallback="Couldn't process that transfer. Please try again."
        />

        {/* DYNAMIC ACTION AREA */}
        {fundingChoice === "card" ? (
          <div className="space-y-4">
            {!transferId ? (
              <button
                type="button"
                onClick={handleInitializeCardTransfer}
                disabled={submitting}
                className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all duration-300 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    <span>Preparing Transfer...</span>
                  </>
                ) : (
                  "Continue to Secure Checkout"
                )}
              </button>
            ) : (
              <PrivyCardOnrampButton transferId={transferId} />
            )}
          </div>
        ) : (
          <button
            onClick={handleBankTransferContinue}
            disabled={submitting}
            className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all duration-300 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                <span>Creating your transfer…</span>
              </>
            ) : (
              "Continue"
            )}
          </button>
        )}
      </div>

      {/* PIN MODAL — required before card funding is prepared for execution */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-6">
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm space-y-4">
            <h2 className="text-lg font-serif font-semibold text-[#0A4730]">
              Enter your PIN
            </h2>
            <p className="text-sm text-[#0A4730]/60">
              Confirm your transaction PIN to continue to secure checkout.
            </p>
            <input
              type="password"
              inputMode="numeric"
              maxLength={12}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder="••••"
              className="w-full text-center text-2xl tracking-widest border border-[#0A4730]/20 rounded-2xl py-3 outline-none focus:border-[#0A4730]"
              autoFocus
            />
            <ErrorNotice
              error={pinError}
              fallback="Couldn't initialize card transaction."
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowPinModal(false);
                  setPin("");
                  setPinError(null);
                }}
                disabled={submitting}
                className="flex-1 h-12 rounded-2xl border border-[#0A4730]/20 font-semibold text-[#0A4730] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCardTransferWithPin}
                disabled={submitting}
                className="flex-1 h-12 rounded-2xl bg-[#0A4730] text-white font-semibold disabled:opacity-50"
              >
                {submitting ? "Verifying..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSenderDetails && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40 sm:items-center sm:justify-center sm:px-6">
          <form
            onSubmit={saveCameroonSenderDetails}
            className="w-full max-w-lg space-y-4 rounded-t-3xl bg-white p-6 sm:rounded-3xl"
          >
            <div>
              <h2 className="text-xl font-serif font-semibold text-[#0A4730]">
                Complete sender details
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-[#0A4730]/60">
                Cameroon mobile-money payouts require these details before you
                fund the transfer.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label>
                <FieldLabel>First name</FieldLabel>
                <input
                  required
                  value={senderDetails.firstName}
                  onChange={(event) =>
                    setSenderDetails((current) => ({
                      ...current,
                      firstName: event.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-[#0A4730]/20 px-3 py-3 text-sm outline-none focus:border-[#0A4730]"
                  autoComplete="given-name"
                />
              </label>
              <label>
                <FieldLabel>Last name</FieldLabel>
                <input
                  required
                  value={senderDetails.lastName}
                  onChange={(event) =>
                    setSenderDetails((current) => ({
                      ...current,
                      lastName: event.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-[#0A4730]/20 px-3 py-3 text-sm outline-none focus:border-[#0A4730]"
                  autoComplete="family-name"
                />
              </label>
            </div>

            <label className="block">
              <FieldLabel>Phone number</FieldLabel>
              <input
                required
                type="tel"
                value={senderDetails.phoneNumber}
                onChange={(event) =>
                  setSenderDetails((current) => ({
                    ...current,
                    phoneNumber: event.target.value,
                  }))
                }
                placeholder="+447700900123"
                className="w-full rounded-xl border border-[#0A4730]/20 px-3 py-3 text-sm outline-none focus:border-[#0A4730]"
                autoComplete="tel"
              />
            </label>

            <label className="block">
              <FieldLabel>Your country</FieldLabel>
              <input
                required
                value={senderDetails.countryCode}
                onChange={(event) =>
                  setSenderDetails((current) => ({
                    ...current,
                    countryCode: event.target.value.toUpperCase(),
                  }))
                }
                maxLength={2}
                placeholder="GB"
                className="w-full rounded-xl border border-[#0A4730]/20 px-3 py-3 text-sm uppercase outline-none focus:border-[#0A4730]"
                autoComplete="country"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label>
                <FieldLabel>Gender</FieldLabel>
                <select
                  required
                  value={senderDetails.gender}
                  onChange={(event) =>
                    setSenderDetails((current) => ({
                      ...current,
                      gender: event.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-[#0A4730]/20 bg-white px-3 py-3 text-sm outline-none focus:border-[#0A4730]"
                >
                  <option value="">Select</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
              </label>
              <label>
                <FieldLabel>Source of funds</FieldLabel>
                <select
                  required
                  value={senderDetails.sourceOfFunds}
                  onChange={(event) =>
                    setSenderDetails((current) => ({
                      ...current,
                      sourceOfFunds: event.target.value,
                    }))
                  }
                  className="w-full rounded-xl border border-[#0A4730]/20 bg-white px-3 py-3 text-sm outline-none focus:border-[#0A4730]"
                >
                  <option value="">Select</option>
                  {ZUBA_XAF_FUNDING_SOURCES.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <ErrorNotice
              error={senderDetailsError}
              fallback="We couldn't save your sender details. Please try again."
            />

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowSenderDetails(false)}
                disabled={isUpdatingMe}
                className="flex-1 rounded-2xl border border-[#0A4730]/20 py-3 font-semibold text-[#0A4730] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdatingMe}
                className="flex-1 rounded-2xl bg-[#0A4730] py-3 font-semibold text-white disabled:opacity-50"
              >
                {isUpdatingMe ? "Saving…" : "Save and continue"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
